import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addMonths, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth } from "date-fns";
import { Plus, Pencil, Trash2, AlertOctagon, Loader2, ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modal";
import { api, fmtTime, fmtDate, formatApiError } from "@/lib/api";

const KINDS = [
  { id: "booking", label: "Booked event", hint: "A confirmed event from your pipeline — blocks the time for visitors", chip: "bg-ink text-bone" },
  { id: "closure", label: "Closed / unavailable", hint: "You're not taking bookings — holiday, maintenance, personal day", chip: "bg-red-100 text-red-800" },
];
const kindOf = (id) => KINDS.find((k) => k.id === id) || KINDS[0];

const emptyForm = { date: "", all_day: false, start_time: "10:00", end_time: "14:00", title: "", kind: "booking", notes: "", buffer_before: 30, buffer_after: 30 };

const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };

export default function CalendarTab() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [selected, setSelected] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [editing, setEditing] = useState(undefined);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const blocksQ = useQuery({
    queryKey: ["admin-blocks", month],
    queryFn: () => api.get("/admin/blocks", { params: { month } }).then((r) => r.data),
  });

  const monthDate = useMemo(() => new Date(`${month}-15T12:00:00`), [month]);
  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(monthDate)), end: endOfWeek(endOfMonth(monthDate)),
  }), [monthDate]);

  const blocks = blocksQ.data?.blocks || [];
  const defaultBuffer = blocksQ.data?.buffer_minutes ?? 30;
  const blocksOn = (iso) => blocks.filter((b) => b.date <= iso && iso <= b.end_date);
  const selectedBlocks = blocksOn(selected);

  const openAdd = (date) => {
    setEditing(null);
    setForm({ ...emptyForm, date: date || selected, buffer_before: defaultBuffer, buffer_after: defaultBuffer });
  };
  const openEdit = (b) => {
    setEditing(b.block_id);
    setForm({ date: b.date, all_day: b.all_day,
      start_time: b.start_time, end_time: b.end_time, title: b.title, kind: KINDS.some((k) => k.id === b.kind) ? b.kind : "booking",
      notes: b.notes || "",
      buffer_before: b.buffer_before ?? defaultBuffer, buffer_after: b.buffer_after ?? defaultBuffer });
  };

  const existingForDate = useMemo(
    () => (form.date ? blocksOn(form.date).filter((b) => b.block_id !== editing) : []),
    [form.date, editing, blocks]
  );

  const conflict = useMemo(() => {
    if (!form.date || existingForDate.length === 0) return null;
    if (form.all_day) return existingForDate[0];
    const cs = toMin(form.start_time) - (Number(form.buffer_before) || 0);
    const ce = toMin(form.end_time) + (Number(form.buffer_after) || 0);
    return existingForDate.find((b) => {
      const bs = b.all_day ? 0 : toMin(b.start_time) - (b.buffer_before || 0);
      const be = b.all_day ? 1440 : toMin(b.end_time) + (b.buffer_after || 0);
      return cs < be && ce > bs;
    }) || null;
  }, [form, existingForDate]);

  const save = async (e) => {
    e.preventDefault();
    if (conflict) return;
    setSaving(true);
    try {
      const payload = { ...form, end_date: null,
        start_time: form.all_day ? null : form.start_time, end_time: form.all_day ? null : form.end_time,
        buffer_before: Number(form.buffer_before) || 0, buffer_after: Number(form.buffer_after) || 0 };
      const r = editing
        ? await api.put(`/admin/blocks/${editing}`, payload)
        : await api.post("/admin/blocks", payload);
      await qc.invalidateQueries({ queryKey: ["admin-blocks"] });
      if (r.data.overlaps?.length) {
        toast.warning("Saved, but it overlaps another block — double-check.");
      } else {
        toast.success(editing ? "Block updated." : "Block added.");
      }
      setEditing(undefined);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove "${b.title}" (${fmtDate(b.date)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blocks/${b.block_id}`);
      await qc.invalidateQueries({ queryKey: ["admin-blocks"] });
      toast.success("Block removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dialogOpen = editing !== undefined;

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <div className="card p-5 sm:p-7" data-testid="admin-calendar-view">
          <div className="flex items-center justify-between">
            <button onClick={() => setMonth(format(addMonths(monthDate, -1), "yyyy-MM"))} aria-label="Previous month"
              data-testid="admin-month-prev" className="rounded-full p-2.5 text-stone-500 hover:bg-bone2"><ChevronLeft className="h-5 w-5" /></button>
            <p className="font-serif text-xl text-ink">{format(monthDate, "MMMM yyyy")}</p>
            <button onClick={() => setMonth(format(addMonths(monthDate, 1), "yyyy-MM"))} aria-label="Next month"
              data-testid="admin-month-next" className="rounded-full p-2.5 text-stone-500 hover:bg-bone2"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="mt-5 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-stone-400">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <span key={d} className="py-1.5">{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const iso = format(d, "yyyy-MM-dd");
              const dayBlocks = blocksOn(iso);
              const inMonth = isSameMonth(d, monthDate);
              return (
                <button key={iso} onClick={() => setSelected(iso)}
                  data-testid={`admin-day-${iso}`}
                  className={`flex min-h-[3.6rem] flex-col items-center gap-1 rounded-xl p-1.5 text-sm transition-colors sm:min-h-[4.5rem]
                    ${!inMonth ? "opacity-30" : ""} ${selected === iso ? "bg-ink text-bone" : "hover:bg-bone2 text-stone-700"}`}>
                  <span className="font-medium">{format(d, "d")}</span>
                  <span className="flex gap-0.5">
                    {dayBlocks.slice(0, 3).map((b) => (
                      <span key={b.block_id} className={`h-1.5 w-1.5 rounded-full ${selected === iso ? "bg-gold" : b.kind === "closure" ? "bg-red-600" : "bg-ink"}`} />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-black/5 pt-4 text-[11px] text-stone-500">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-ink" /> Booked event</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-600" /> Closed / unavailable</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="card p-6 sm:p-7">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="overline-label">{fmtDate(selected)}</p>
              <p className="mt-1 font-serif text-xl text-ink">{selectedBlocks.length ? `${selectedBlocks.length} block${selectedBlocks.length > 1 ? "s" : ""}` : "No blocks"}</p>
            </div>
            <button onClick={() => openAdd(selected)} data-testid="admin-calendar-block-btn" className="btn-dark !px-5 !py-2.5 text-xs">
              <Plus className="h-4 w-4" /> Add Block
            </button>
          </div>
          <div className="mt-6 space-y-3">
            {blocksQ.isLoading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-stone-400" />}
            {!blocksQ.isLoading && selectedBlocks.length === 0 && (
              <p className="rounded-xl bg-bone2 p-4 text-sm text-stone-500">This day is open. Add a block when an event is confirmed (Deposit Paid in your pipeline) or to close the date.</p>
            )}
            {selectedBlocks.map((b) => (
              <div key={b.block_id} className="rounded-2xl border border-black/5 bg-white p-4" data-testid={`admin-block-${b.block_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${kindOf(b.kind).chip}`}>
                      {kindOf(b.kind).label}
                    </span>
                    <p className="mt-2.5 font-serif text-lg text-ink">{b.title}</p>
                    <p className="mt-1 text-xs text-stone-500">
                      {b.all_day ? "All day" : `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`}
                    </p>
                    {!b.all_day && (b.buffer_before > 0 || b.buffer_after > 0) && (
                      <p className="mt-1 text-[11px] font-medium text-amber-700">
                        Reserved on calendar: {fmtTime(b.start_time)} minus {b.buffer_before}m → {fmtTime(b.end_time)} plus {b.buffer_after}m
                      </p>
                    )}
                    {b.notes && <p className="mt-2 text-xs italic text-stone-500">{b.notes}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(b)} aria-label={`Edit ${b.title}`} data-testid={`admin-block-edit-${b.block_id}`}
                      className="rounded-full p-2 text-stone-400 transition-colors hover:bg-bone2 hover:text-ink"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(b)} aria-label={`Delete ${b.title}`} data-testid={`admin-block-delete-${b.block_id}`}
                      className="rounded-full p-2 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal open={dialogOpen} onClose={() => setEditing(undefined)} title={editing ? "Edit block" : "Block off time"} testid="admin-block-modal">
        <form onSubmit={save} className="space-y-5">
          <div>
            <label htmlFor="blk-date" className="label">Date *</label>
            <input id="blk-date" type="date" required value={form.date} data-testid="admin-block-date"
              onChange={(e) => setForm({ ...form, date: e.target.value })} className="field" />
          </div>

          {existingForDate.length > 0 && (
            <div className="rounded-xl bg-bone2 p-4" data-testid="admin-existing-blocks">
              <p className="label !mb-2 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Already on {form.date ? fmtDate(form.date) : "this day"}</p>
              <div className="space-y-2">
                {existingForDate.map((b) => (
                  <div key={b.block_id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3.5 py-2 text-xs">
                    <span className="font-medium text-stone-700">
                      {b.all_day ? "All day" : `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`}
                      {(b.buffer_before > 0 || b.buffer_after > 0) && !b.all_day && (
                        <span className="text-amber-700"> +{b.buffer_before}/{b.buffer_after}m buffer</span>
                      )}
                    </span>
                    <span className="truncate text-stone-500">{b.title}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-stone-400">These times (including buffers) can't be booked again.</p>
            </div>
          )}

          <div>
            <label htmlFor="blk-title" className="label">Private title *</label>
            <input id="blk-title" required value={form.title} data-testid="admin-block-title"
              onChange={(e) => setForm({ ...form, title: e.target.value })} className="field"
              placeholder="e.g. Martinez baby shower — 60 guests" />
            <p className="mt-1.5 text-[11px] text-stone-400">Never shown publicly — visitors only see “Unavailable”.</p>
          </div>

          <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-700">
            <input type="checkbox" checked={form.all_day} data-testid="admin-block-allday"
              onChange={(e) => setForm({ ...form, all_day: e.target.checked })} className="h-4 w-4 rounded accent-[#1C1917]" />
            Block the whole day
          </label>

          {!form.all_day && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="blk-start" className="label">Event start *</label>
                  <input id="blk-start" type="time" required step="900" value={form.start_time} data-testid="admin-block-start"
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="field" />
                </div>
                <div>
                  <label htmlFor="blk-end" className="label">Event end *</label>
                  <input id="blk-end" type="time" required step="900" value={form.end_time} data-testid="admin-block-end"
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="field" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="blk-buffer-before" className="label">Buffer before (min)</label>
                  <input id="blk-buffer-before" type="number" min="0" step="15" value={form.buffer_before} data-testid="admin-block-buffer-before"
                    onChange={(e) => setForm({ ...form, buffer_before: e.target.value })} className="field" />
                </div>
                <div>
                  <label htmlFor="blk-buffer-after" className="label">Buffer after (min)</label>
                  <input id="blk-buffer-after" type="number" min="0" step="15" value={form.buffer_after} data-testid="admin-block-buffer-after"
                    onChange={(e) => setForm({ ...form, buffer_after: e.target.value })} className="field" />
                </div>
              </div>
              <p className="-mt-2 text-[11px] leading-relaxed text-stone-400">
                {defaultBuffer} minutes each side is included with every booking for setup and cleanup.
                Extend it here when a client purchases extra time.
              </p>
            </>
          )}

          <div>
            <span className="label">What is this block?</span>
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Block type">
              {KINDS.map((k) => (
                <button type="button" key={k.id} onClick={() => setForm({ ...form, kind: k.id })}
                  data-testid={`admin-block-kind-${k.id}`} aria-pressed={form.kind === k.id}
                  className={`rounded-xl border p-3.5 text-left transition-colors ${form.kind === k.id ? "border-ink bg-bone2" : "border-black/10 hover:border-black/25"}`}>
                  <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${k.chip}`}>{k.label}</span>
                  <p className="mt-2 text-[11px] leading-relaxed text-stone-500">{k.hint}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="blk-notes" className="label">Notes (private)</label>
            <textarea id="blk-notes" rows={2} value={form.notes} data-testid="admin-block-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} className="field"
              placeholder="Deposit received, contact, extra time purchased…" />
          </div>

          {conflict && (
            <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4 text-xs leading-relaxed text-red-800 ring-1 ring-red-700/20" role="alert" data-testid="admin-block-conflict">
              <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <span className="font-bold">This can't be booked.</span> It overlaps “{conflict.title}”
                {conflict.all_day ? " (all day)" : ` (${fmtTime(conflict.start_time)} – ${fmtTime(conflict.end_time)}${conflict.buffer_before || conflict.buffer_after ? ", plus buffer" : ""})`}.
                Pick a different time or day.
              </span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving || !!conflict} data-testid="admin-block-save"
              className="btn-dark flex-1 disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add block"}
            </button>
            <button type="button" onClick={() => setEditing(undefined)} className="btn-ghost !px-5 !py-2.5 text-xs">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
